import {
  LoaderFunction,
  redirect,
  useLoaderData,
  useNavigate,
} from "react-router-dom";

import { get } from "api";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { ProductCardLarge } from "molecules/cards/product-card-large/ProductCardLarge";
import TopNav from "molecules/navbars/TopNav";
import { queryClient } from "pages/Root";
import { FilterAndSortListings } from "pages/root/shop/FilterAndSortListings";
import { useFeatureFlag } from "services/Flags";
import { hasLinearPricing } from "services/ListingHelperService";
import { getListingsWithDiscountInformation } from "services/ListingHelperService";
import { notNull } from "toolbox/Guards";
import { waitObj } from "toolbox/Promise";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";

const load = async (concernId: string) => {
  const shopData = queryClient.fetchQuery(["/shop"], () => get("/shop"));
  const listings = queryClient.fetchQuery(
    ["/listings"],
    () => get("/listings"),
    { staleTime: 1000 * 60 }
  );
  const shopDiscounts = queryClient.fetchQuery(
    ["/shop/discounts"],
    () => get("/shop/discounts"),
    { staleTime: 1000 * 10 }
  );
  const customShopCategories = queryClient.fetchQuery(
    ["/shop/custom-categories/listings"],
    () => get("/shop/custom-categories/listings"),
    { staleTime: 1000 * 10 }
  );
  const user = queryClient.fetchQuery({
    queryKey: ["/user"],
    queryFn: () => get("/user"),
    staleTime: 1000 * 60 * 2,
  });

  return waitObj({
    concernId,
    shopData,
    listings,
    shopDiscounts,
    customShopCategories,
    user,
  });
};

type LoaderData = Awaited<ReturnType<typeof load>>;
export const shopByConcernLoader: LoaderFunction = ({ params: { id } }) => {
  if (!id) return redirect("/shop");
  return load(id);
};

const ShopByConcernPage = () => {
  const {
    concernId,
    shopData,
    listings,
    shopDiscounts,
    customShopCategories,
    user,
  } = useLoaderData() as LoaderData;
  const navigate = useNavigate();

  const concern = shopData.concerns?.find((concern) => concern.id == concernId);
  let filteredListings = listings.filter((listing) =>
    listing.concernIds.includes(concernId)
  );
  filteredListings = getListingsWithDiscountInformation(
    filteredListings,
    customShopCategories,
    shopDiscounts?.listingDiscounts
  );
  const filteredConcerns =
    shopData.concerns?.filter((concern) =>
      filteredListings
        .flatMap((listing) => listing.concernIds)
        .includes(concern.id)
    ) ?? [];

  return (
    <>
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate("/shop/concerns")}
      >
        <div className="flex max-h-[32px] items-center">
          <h1 className="text-xl">By Concern</h1>
        </div>
      </TopNav>
      <div className="flex h-full flex-col bg-two">
        {concern && concern.name && (
          <ShopListHeader
            title={concern.name}
            subtitle={concern.description ?? ""}
            bgGradient
            backgroundColor="rgb(var(--repeatmd-brand-color))"
          />
        )}
        {filteredListings && (
          <FilterAndSortListings
            types={["Treatment", "Package"]}
            areas={[]}
            concerns={[]}
            listings={filteredListings}
          >
            {(listing) => (
              <ProductCardLarge
                key={listing.id}
                itemId={listing.id}
                title={listing.title}
                description={listing.description}
                imageUrl={listing.imageUrl}
                type={listing.type}
                tag={""}
                concerns={filteredConcerns
                  .filter((concern) => listing.concernIds.includes(concern.id))
                  .map((concern) => concern.name)
                  .filter(notNull)}
                price={listing.baseCost}
                memberPrice={listing.membershipCost}
                onClick={() => {
                  navigate(`/pdp/package/${listing.id}`);
                }}
                hasLinearPricing={hasLinearPricing(
                  listing.packages?.flatMap((p) => p.packagePrices)
                )}
                unitType={listing.packageUnitType?.singularUnitType}
                discount={listing.discountText}
                customColor={listing.customColor}
                guestMode={user.isGuest}
              />
            )}
          </FilterAndSortListings>
        )}
        <RepeatMDFooter />
      </div>
    </>
  );
};

export default ShopByConcernPage;
